import { Search, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { setSearchQuery } from '../../slice/ImageAssetSlice';
import { FILTER_LOCK_TOOLTIP } from '../../model/assetViewScope';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';

type AssetsSearchFieldProps = {
  /** Sub-lg only: whether the control row has swapped itself for this field. */
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** Zillow 3D renders the bar's controls inert rather than removing them. */
  disabled?: boolean;
  /**
   * A photo is checked, so the view filters are frozen (decided 2026-09-18).
   * Distinct from `disabled`: the field stays in the a11y tree and keeps its
   * clear-X live — see the comment on the readonly treatment below.
   */
  locked?: boolean;
};

/**
 * DV-2856 grid search. One node, three presentations:
 *
 * - desktop: an inline field in the control row;
 * - sub-lg collapsed: an icon button that tints while a query is active, so a
 *   filtered grid is never unexplained once the row scrolls away;
 * - sub-lg expanded: a full-width field with a Done control, which
 *   AssetsControlBar renders in place of the whole control row (the row must
 *   keep constant height — see its own comment about scroll feedback).
 *
 * Done closes the row but deliberately keeps the query: dismissing the input
 * is not the same as clearing the filter, and silently unfiltering would hide
 * the fact that a search ever ran.
 *
 * `locked` is the selection lock (decided 2026-09-18): while any photo is
 * checked the query cannot change, so the selection stays a subset of what is
 * displayed. It is `readonly` + `aria-disabled`, NOT `disabled` — a disabled
 * input drops out of the accessibility tree and takes the user's own query
 * with it, and the query must stay readable. The clear-X is exempt: clearing
 * WIDENS the pool, so the invariant still holds, and dimming a control that
 * works would read as broken.
 */
const AssetsSearchField = ({
  expanded,
  onExpandedChange,
  disabled = false,
  locked = false
}: AssetsSearchFieldProps) => {
  const dispatch = useDispatch();
  const query = useSelector((state: RootState) => state.imageAsset.searchQuery);
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT_QUERY);

  const field = (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2 rounded-md border border-input px-3',
        // Desktop: 230px when the row has room, shrinking to 150px when it
        // doesn't (1024–1100px with the sidebar open) — a shrink-0 field here
        // overlapped the service pills' trigger (DV-3193 QA).
        isDesktop ? 'h-9 w-[230px] min-w-[150px]' : 'h-11 flex-1',
        locked ? 'bg-muted cursor-not-allowed' : 'bg-background'
      )}
    >
      {/* The dim stops here rather than wrapping the row: opacity cascades, so
          a dimmed parent would take the still-live clear-X down with it. */}
      <div
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2',
          locked && 'opacity-45'
        )}
      >
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <Input
          value={query}
          disabled={disabled}
          readOnly={locked}
          aria-disabled={locked || undefined}
          autoFocus={!isDesktop}
          onChange={(e) => dispatch(setSearchQuery(e.target.value))}
          placeholder="Search Caption or ID…"
          aria-label="Search Photos by Caption or Image ID"
          className={cn(
            'h-full min-w-0 flex-1 border-none bg-transparent px-0 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0',
            locked && 'cursor-not-allowed'
          )}
        />
      </div>
      {query !== '' && (
        <button
          type="button"
          aria-label="Clear Search"
          onClick={() => dispatch(setSearchQuery(''))}
          className="inline-flex shrink-0 items-center justify-center rounded-full bg-muted p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );

  // The tooltip explains the freeze; it wraps the whole box so hovering the
  // inert part answers "why can't I type here", and focusing the readonly
  // input surfaces it without a pointer.
  // Its own provider: the control row supplies one, but this field is also
  // mounted on its own in tests, and a Tooltip without one throws.
  const lockable = locked ? (
    <TooltipProvider>
      <Tooltip delayDuration={250}>
        <TooltipTrigger asChild>{field}</TooltipTrigger>
        <TooltipContent side="bottom">{FILTER_LOCK_TOOLTIP}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    field
  );

  if (isDesktop) return lockable;

  if (expanded) {
    return (
      <>
        {lockable}
        <Button
          variant="ghost"
          className="shrink-0 font-semibold"
          onClick={() => onExpandedChange(false)}
        >
          Done
        </Button>
      </>
    );
  }

  // Sub-lg collapsed trigger. Deliberately NOT locked: opening the field
  // changes no filter, and it is the only way to read back — or clear — a query
  // while a selection holds it. The field it opens is readonly.
  return (
    <Button
      variant="outline"
      size="icon"
      aria-label="Search Photos"
      disabled={disabled}
      onClick={() => onExpandedChange(true)}
      className={cn('shrink-0', query !== '' && 'border-primary text-primary')}
    >
      <Search className="h-4 w-4" />
    </Button>
  );
};

export default AssetsSearchField;
